import { useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { QUERY_KEYS } from 'constants/constants'
import { getJobCategory } from '../../constants/job-categories'
import { getLanguage } from '../../constants/languages'
import trackEvents from '../../constants/track-events'
import {
  convertNumberToTime,
  notifyBugsnag,
  sendSlackMessage
} from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
// import { usePaymentRestrictions } from '../../hooks/use-payment-restrictions'
import { ActivityIndicator } from '..'
import { UPDATE_STATUS } from '../../constants/career-history'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext,
  usePartnerContext,
  usePaymentContext,
  useProductTourContext
} from '../../context'
import { useSelectionParams } from '../../features/discover/hooks/useSelectionParams'
import { MODES } from '../../features/pricing/entitlements'
import { useLimitGuards } from '../../features/pricing/guards'
import {
  invalidatePricingUsage,
  useEntitlements
} from '../../features/pricing/hooks'
import { LIMIT_DIALOG_ACTIONS } from '../../features/pricing/pricing-dialog-context'
import { replacesCountedCareer } from '../../features/pricing/usage/careers'
import { mutation, query } from '../../graphql'
import { getDifficultyOptions } from '../../helpers/diagnostic'
import { useCreateCareerNavigation } from '../../hooks/use-create-career-navigation'
import { useCreateLog } from '../../hooks/use-create-log'
import {
  LIFE_CYCLE_STAGES,
  useLifecycleStageTracking
} from '../../hooks/useLifecycleStageTracking'

import { useMount } from '../../hooks/use-mount'
import Button from '../tailwind/Button'
import CreateCareerFooter from './CreateCareerFooter'
import RiasecRequirementModal from './adjustment/riasec-requirement-modal'
import CareerLimitHint from './career-limit-hint'
import ExternalIdAlert from './pieces/ExternalIdAlert'

// TODO: go on here
// b / dashboard / activate career / Auswirkungen fehlen
const Overview = ({ career, careerMutation, careers }) => {
  const { t } = useTranslation()

  const { language } = useLanguageContext()
  const { is_trial } = usePaymentContext()
  const { system } = useAivyContext()
  const { partner } = usePartnerContext()
  const { alert } = useNotificationContext()

  const { trackLifecycleStage } = useLifecycleStageTracking()

  const { addCareer } = useSelectionParams()

  const { mode } = useEntitlements()
  const { guardCareerActivation } = useLimitGuards()
  const [isCheckingLimit, setIsCheckingLimit] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    setProductTourState,
    productTourState: { tourActive }
  } = useProductTourContext()

  const hasTrackedStep5View = useRef(false)

  useMount(() => {
    if (tourActive) {
      setTimeout(() => {
        setProductTourState({ run: true })
      }, 0.4 * 1000)
    }
  })

  const { createCareerHistoryLog } = useCreateLog()

  const { isLoadingBack, handleBackNavigation } = useCreateCareerNavigation({
    career,
    careerMutation
  })

  // Unbekannte exam_ids ueberspringen statt crashen: system.challenges kommt
  // aus dem Backend-System-Item CHALLENGES. Fehlt dort ein Eintrag (z.B.
  // CULTURE in einer nicht geseedeten Stage), riss der ungepruefte Zugriff
  // ueber die globale ErrorBoundary das ganze Dashboard mit.
  const challenges = JSON.parse(career.app_settings.challenges).filter(
    ({ exam_id }) => !!system.challenges[exam_id]
  )

  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [displayRiasecRequirement, setDisplayRiasecRequirement] = useState(
    challenges.some(({ exam_id }) => exam_id === 'RIASEC_IMAGES')
  )

  const trackOnboardingCareerCreated = () => {
    const storageKey = 'onboarding-career-created'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.ONBOARDING_CAREER_CREATED,
      lifecycle_stage_before: LIFE_CYCLE_STAGES.ONBOARDING_STARTED,
      aivy_form_id: partner.aivy_form_id,
      lead_progress: (2 / 5).toFixed(2),
      partner_id: partner.id
    })
    localStorage.setItem(storageKey, 'true')
  }

  const career_analyse = JSON.parse(career.career_analyse)

  const { data: careerAnalyses, status: careerAnalysesStatus } = useQuery({
    queryKey: QUERY_KEYS.analysesByCareer(career.id),
    queryFn: () =>
      query({
        query: 'careerAnalyseByCareer',
        variables: { career_id: career.id }
      }),
    select: (response) =>
      response.map((item) => ({
        ...item,
        preset: item.preset ? JSON.parse(item.preset) : null
      }))
  })

  if (careerAnalysesStatus === 'pending') {
    return <ActivityIndicator />
  }

  const foundCareerWithExternalCustomID = careers.find(
    ({ status, external_custom_id }) => {
      return (
        status === 'ACTIVE' && external_custom_id === career.external_custom_id
      )
    }
  )

  const overview = [
    {
      title: t('create_career.overview.career_title_title'),
      value: career.title
    },
    career.external_custom_id && {
      title: t('create_career.overview.external_custom_id_title'),
      value: career.external_custom_id,
      Info: foundCareerWithExternalCustomID
        ? (props) => (
            <ExternalIdAlert
              {...props}
              old_career_title={foundCareerWithExternalCustomID.title}
              new_career_title={career.title}
              external_custom_id={career.external_custom_id}
            />
          )
        : undefined
    },
    career.language && {
      title: t('create_career.overview.language_title'),
      value: t(getLanguage(career.language))
    },
    {
      title: t('create_career.overview.category_title'),
      value: t(getJobCategory(career.category))
    },
    {
      title: t('create_career.overview.analyses_title'),
      value: careerAnalyses
        .map(({ person, preset }) => {
          if (preset) return preset.name[language]
          if (!person) return '---'
          return person.email || person.invitation_email || '---'
        })
        .join(', ')
    }
  ]

  const { minutes, seconds } = convertNumberToTime(
    challenges
      .map(({ exam_id }) => system.challenges[exam_id].timeToComplete)
      .reduce(
        (previousValue, currentValue) => previousValue + currentValue,
        0
      ) / 60000
  )

  const getSelectionDifficulty = () => {
    const selection_difficulty = career_analyse.selection_difficulty ?? 'NORMAL'

    const option = getDifficultyOptions().find(
      ({ difficulty }) => difficulty === selection_difficulty
    )

    return t(option.label)
  }

  const mandatoryCriterions = Object.entries(career_analyse.scores)
    .filter(([_, value]) => value.mandatoryCriterion === true)
    .map(([key]) => key)

  const getMandatoryCriterions = () => {
    const { dimensions_pdf_reports: dimensions } = system

    return mandatoryCriterions
      .map((dimension_key) => dimensions[dimension_key].property[language])
      .join(', ')
  }

  overview.push(
    {
      title: t('create_career.overview.challenges_title'),
      value: challenges
        .map(({ exam_id }) => system.challenges[exam_id].title[language])
        .join(', ')
    },
    {
      title: t('create_career.overview.duration_title'),
      value: t('create_career.overview.duration_value', {
        minutes,
        seconds
      })
    },
    {
      title: t('create_career.overview.selection_difficulty_title'),
      value: getSelectionDifficulty()
    },
    !!mandatoryCriterions.length && {
      title: t('create_career.overview.must_criteria_title'),
      value: getMandatoryCriterions()
    }
  )

  const trackFirstCareerCreated = () => {
    const storageKey = 'firstCareerCreatedTracked'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.FIRST_CAREER_CREATED,
      lifecycle_stage_before: LIFE_CYCLE_STAGES.ONBOARDING_COMPLETED,
      aivy_form_id: partner.aivy_form_id,
      lead_progress: (6 / 5).toFixed(2),
      partner_id: partner.id
    })
    localStorage.setItem(storageKey, 'true')
  }

  const activateCareer = async () => {
    setIsLoadingNext(true)

    // rm override_defaults
    await mutation({
      mutation: 'updateCareer',
      input: {
        id: career.id,
        career_analyse: JSON.stringify({
          ...JSON.parse(career.career_analyse),
          override_defaults: undefined
        })
      }
    })

    await createCareerHistoryLog({
      career_id: career.id,
      context: UPDATE_STATUS,
      input: { status: 'ACTIVE' }
    })

    const response = await query({
      query: 'careerControl',
      variables: { action: 'activateCareer', career_id: career.id }
    })

    const { name: partner_name } = partner
    const { id: activated_career_id, title: career_title } =
      JSON.parse(response).activateCareer.res

    if (!activated_career_id.includes('sample')) {
      trackEvent(trackEvents.FINISH_CREATE_CAREER)

      if (is_trial) {
        queryClient.refetchQueries({
          queryKey: QUERY_KEYS.trialDataByPartner(partner.id),
          type: 'active'
        })
      }

      sendSlackMessage(
        'kunden-controlling',
        `Die Stelle *${career_title}* (${activated_career_id}) von _${partner_name}_ ${
          is_trial ? '(TRIAL)' : ''
        } ist nun aktiv.`
      )
    }

    // Die Stelle belegt jetzt einen Platz, Banner und Meter zählen neu
    invalidatePricingUsage({
      queryClient,
      partnerId: partner.id,
      resource: 'careers'
    })

    // invalidate cache because career_id has changed
    if (activated_career_id !== career.id) queryClient.removeQueries()

    setIsLoadingNext(false)

    trackFirstCareerCreated()

    const returnData = localStorage.getItem('career-creation-return')

    if (returnData) {
      try {
        const { returnType } = JSON.parse(returnData)

        if (returnType === 'career') {
          localStorage.removeItem('career-creation-return', {
            showAssistant: true,
            careerCreated: true
          })

          trackOnboardingCareerCreated()

          addCareer(activated_career_id, {
            showAssistant: true,
            careerCreated: true
          })
        }
      } catch (error) {
        notifyBugsnag(error)

        localStorage.removeItem('career-creation-return')
      }
    } else {
      navigate(`/career/${activated_career_id}`, { replace: true })
    }
  }

  const onFinishOverview = async () => {
    // Erst den Platz prüfen, dann bestätigen lassen. Ist keiner frei, öffnet
    // der Guard den Limit-Dialog, und der Entwurf bleibt, wie er ist. Außerhalb
    // von enforced gibt der Guard sofort frei, dort gibt es keinen Ladezustand.
    setIsCheckingLimit(mode === MODES.ENFORCED)
    const allowed = await guardCareerActivation(career, {
      source: 'create_career_overview',
      action: LIMIT_DIALOG_ACTIONS.ACTIVATE
    })
    setIsCheckingLimit(false)
    if (!allowed) return

    alert(
      t('create_career.overview.submit_modal_title'),
      [t('create_career.overview.submit_modal_description')],
      [
        {
          onPress: activateCareer,
          text: t('create_career.overview.submit_modal_confirm_action')
        },
        {
          text: t('create_career.overview.submit_modal_cancel_action'),
          style: 'cancel'
        }
      ]
    )
  }

  const handleBack = () => {
    const { expert_mode_used } = JSON.parse(career.customer_dashboard_meta)

    if (expert_mode_used) handleBackNavigation({ backStatus: 'WAIT_CONFIG' })
    else handleBackNavigation({ backStatus: 'WAIT_CONFIRM_TESTBATTERY' })
  }

  if (displayRiasecRequirement) {
    return (
      <RiasecRequirementModal
        career={career}
        careerMutation={careerMutation}
        careerAnalyses={careerAnalyses}
        back={handleBack}
        onFinish={() => setDisplayRiasecRequirement(false)}
      />
    )
  }

  // Track step 5 view only once, and only when overview is actually shown
  if (!hasTrackedStep5View.current) {
    trackEvent(trackEvents.CREATE_CAREER_STEP_5_VIEWED)
    hasTrackedStep5View.current = true
  }

  return (
    <>
      <h1 className='text-3xl font-bold tracking-tight text-gray-900'>
        {t('create_career.overview.title')}
      </h1>
      <p className='mt-3 text-base text-gray-600'>
        {t('create_career.overview.description')}
      </p>
      <div className='mt-8 border-t border-gray-200'>
        <dl className='divide-y divide-gray-200'>
          {overview
            .filter((item) => item)
            .map(({ title, value, Info }, index) => (
              <div
                key={index}
                className='py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5'
              >
                <dt className='my-auto text-sm text-gray-700'>{title}</dt>
                <dd className='mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0'>
                  <span className='flex-grow'>{value}</span>
                </dd>
                {!!Info && <Info className='sm:col-span-3' />}
              </div>
            ))}
        </dl>
      </div>
      {/* Dieselbe Regel wie der Guard. Eine archivierte Stelle mit derselben
          external_custom_id ersetzt nichts, das Aktivieren kostet dann einen
          Platz. */}
      <CareerLimitHint
        replacesActiveCareer={replacesCountedCareer({ career, careers })}
        source='create_career_overview'
        className='mt-6'
      />
      <CreateCareerFooter>
        <div className='flex justify-end gap-x-2'>
          <Button.SecondaryXL
            onClick={handleBack}
            isLoading={isLoadingBack}
            text={t('create_career.back_action')}
          />
          <Button.PrimaryXL
            text={t('create_career.overview.submit_action')}
            isLoading={isLoadingNext || isCheckingLimit}
            onClick={onFinishOverview}
          />
        </div>
      </CreateCareerFooter>
    </>
  )
}

Overview.propTypes = {
  career: PropTypes.object.isRequired,
  careerMutation: PropTypes.object.isRequired,
  careers: PropTypes.array.isRequired
}

export default Overview
